import React, { ChangeEventHandler } from 'react';
import { Form } from 'react-bootstrap';

/**
 * Checks if a subtitle text is a valid VTT (must start with WEBVTT).
 */
function isValidVtt(text: string): boolean {
    return text.trimStart().startsWith('WEBVTT');
}

interface SubtitleInputProps {
    /** Called when a valid .vtt file is selected */
    onValidFile: (file: File) => void;
    /** Called with an error message when validation fails */
    onError: (msg: string) => void;
}

/**
 * SubtitleInput: file input and VTT validation.
 * Expects to be wrapped in a Form.Group for layout.
 */
export function SubtitleInput({ onValidFile, onError }: SubtitleInputProps) {
    const handleChange: ChangeEventHandler<HTMLInputElement> = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.name.toLowerCase().endsWith('.vtt')) {
            onError('Available only *.vtt format');
            return;
        }
        try {
            const text = await file.text();
            if (!isValidVtt(text)) {
                onError('Invalid VTT file: must start with "WEBVTT"');
                return;
            }
            onValidFile(file);
        } catch {
            onError('Error reading subtitle file');
        }
    };

    return (
        <>
            <Form.Label>Subtitles (optional)</Form.Label>
            <Form.Control type="file" accept=".vtt" onChange={handleChange} />
            <Form.Text muted>
                Upload a .vtt subtitle file. Optional but recommended.
            </Form.Text>
        </>
    );
}